import { h, Component } from 'preact';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';

import { createGame, DIFFICULTIES, reveal, toggleFlag } from '../../lib/arcade/minesweeperEngine';
import { formatTime } from '../../lib/arcade/formatTime';
import ArcadeLeaderboard from './leaderboard';
import './minesweeper.css';

function Flag() {
    return <svg viewBox='0 0 20 20' aria-hidden='true'>
        <path d='M10 3v12M5 17h11M7 15h7' stroke='#111' stroke-width='2' />
        <path d='M10 3L3 7l7 3z' fill='#ed2024' />
    </svg>;
}

function Mine() {
    return <svg viewBox='0 0 20 20' aria-hidden='true'>
        <path d='M10 1v18M1 10h18M3 3l14 14M3 17L17 3' stroke='#111' stroke-width='2' />
        <circle cx='10' cy='10' r='6' fill='#111' /><path d='M6 6h3v3H6z' fill='#fff' />
    </svg>;
}

function Face({ status, pressing }) {
    return <svg viewBox='0 0 32 32' aria-hidden='true'>
        <circle cx='16' cy='16' r='12' fill='#ffed00' stroke='#111' stroke-width='1.5' />
        {status === 'lost' ? <path d='M10 10l4 4m-4 0l4-4m4 0l4 4m-4 0l4-4' stroke='#111' stroke-width='2' />
            : status === 'won' ? <path d='M8 10h7v6H9zm9 0h7l-1 6h-6zM15 12h2' fill='#111' />
                : <g fill='#111'><rect x='10' y='10' width='3' height='4' />
                    <rect x='19' y='10' width='3' height='4' /></g>}
        {pressing ? <ellipse cx='16' cy='21' rx='3' ry='4' fill='#111' />
            : <path d={status === 'lost' ? 'M10 23q6-7 12 0' : 'M10 20q6 8 12 0'}
                stroke='#111' stroke-width='2' fill='none' />}
    </svg>;
}

/** Classic Minesweeper (C). The board and pending API responses belong to one run. */
@observer
class Minesweeper extends Component {
    constructor(props) {
        super(props);
        this.game = createGame();
        this.state = {
            revision: 0, elapsedMs: 0, paused: false, pressing: false, flagMode: false, focused: 0,
            submitting: false, saveFailed: false, loadFailed: false, result: null,
            leaderboard: { data: [], me: null },
        };
        this.accrued = 0;
        this.startedAt = null;
        this.timer = null;
        this.paused = false;
        this.unmounted = false;
        this.left = false;
        this.submittedGame = null;
        this.scoreRequest = 0;
        this.cellRefs = [];
    }

    componentDidMount() {
        window.addEventListener('blur', this.pause);
        window.addEventListener('keydown', this.onKeyDown);
        window.addEventListener('pointerup', this.releaseFace);
        window.addEventListener('pointercancel', this.releaseFace);
        document.addEventListener('visibilitychange', this.onVisibility);
        this.loadScores(this.game);
    }

    componentWillUnmount() {
        this.unmounted = true;
        this.stopClock();
        window.removeEventListener('blur', this.pause);
        window.removeEventListener('keydown', this.onKeyDown);
        window.removeEventListener('pointerup', this.releaseFace);
        window.removeEventListener('pointercancel', this.releaseFace);
        document.removeEventListener('visibilitychange', this.onVisibility);
    }

    elapsed = () => this.accrued + (this.startedAt === null ? 0 : Math.max(0, performance.now() - this.startedAt));

    startClock = () => {
        this.startedAt = performance.now();
        this.timer = setInterval(() => {
            const elapsedMs = this.elapsed();
            if (Math.floor(elapsedMs / 1000) !== Math.floor(this.state.elapsedMs / 1000)) {
                this.setState({ elapsedMs });
            }
        }, 200);
    };

    stopClock = () => {
        this.accrued = this.elapsed();
        this.startedAt = null;
        clearInterval(this.timer);
        this.timer = null;
    };

    current = game => !this.unmounted && !this.left && this.game === game;

    loadScores = async (game) => {
        const request = ++this.scoreRequest;
        try {
            const response = await this.props.stores.arcadeScoreStore?.loadLeaderboard(game.game);
            if (!this.current(game) || request !== this.scoreRequest) return;
            this.setState({
                loadFailed: response?.status !== 200,
                leaderboard: response?.status === 200 ? response : { data: [], me: null },
            });
        } catch (error) {
            if (this.current(game) && request === this.scoreRequest) this.setState({ loadFailed: true });
        }
    };

    refresh = () => this.setState(previous => ({
        revision: previous.revision + 1, elapsedMs: this.elapsed(), pressing: false,
    }));

    reset = (difficulty = this.game.key) => {
        this.stopClock();
        this.game = createGame({ difficulty });
        this.accrued = 0;
        this.paused = false;
        this.submittedGame = null;
        this.setState(previous => ({
            revision: previous.revision + 1, elapsedMs: 0, paused: false, pressing: false,
            focused: 0, submitting: false, saveFailed: false, result: null,
            flagMode: false, loadFailed: false, leaderboard: { data: [], me: null },
        }));
        if (this.scrollRef) this.scrollRef.scrollLeft = 0;
        this.loadScores(this.game);
    };

    restart = () => this.reset();

    changeDifficulty = e => this.reset(e.currentTarget.dataset.difficulty);

    actOnCell = (index, flag = false) => {
        if (this.paused || this.left || this.game.status === 'won' || this.game.status === 'lost') return;
        const cell = this.game.cells[index];
        if (!cell) return;
        if (flag) toggleFlag(this.game, index);
        else {
            if (!this.game.armed && !cell.flagged) this.startClock();
            reveal(this.game, index);
        }
        if (this.game.status === 'won' || this.game.status === 'lost') this.stopClock();
        this.refresh();
        if (this.game.status === 'won') this.submitWin();
    };

    revealCell = e => this.actOnCell(Number(e.currentTarget.dataset.cell), this.state.flagMode);

    flagCell = (e) => {
        e.preventDefault();
        this.actOnCell(Number(e.currentTarget.dataset.cell), true);
    };

    submitWin = async () => {
        const game = this.game;
        if (game.status !== 'won' || this.submittedGame === game) return;
        this.submittedGame = game;
        const durationMs = Math.max(1, Math.round(this.accrued));
        this.accrued = durationMs;
        this.setState({ elapsedMs: durationMs, submitting: true });
        try {
            const response = await this.props.stores.arcadeScoreStore?.submitScore({
                game: game.game, score: durationMs, durationMs, wave: 1, won: true,
            });
            if (!this.current(game)) return;
            const saved = response?.status === 200 || response?.status === 201;
            this.setState({ submitting: false, saveFailed: !saved, result: saved ? response.data : null });
            if (saved) await this.loadScores(game);
        } catch (error) {
            if (this.current(game)) this.setState({ submitting: false, saveFailed: true });
        }
    };

    pause = () => {
        if (this.game.status !== 'playing' || this.paused || this.left) return;
        this.stopClock();
        this.paused = true;
        this.setState({ paused: true, pressing: false, elapsedMs: this.accrued });
    };

    resume = () => {
        if (!this.paused || this.left) return;
        this.paused = false;
        this.startClock();
        this.setState({ paused: false }, () => this.cellRefs[this.state.focused]?.focus({ preventScroll: true }));
    };

    onVisibility = () => { if (document.hidden) this.pause(); };

    onKeyDown = (e) => {
        if (e.code !== 'KeyP' || e.repeat) return;
        e.preventDefault();
        if (this.paused) this.resume();
        else this.pause();
    };

    onCellKey = (e) => {
        const index = Number(e.currentTarget.dataset.cell);
        const { cols, rows } = this.game;
        const row = Math.floor(index / cols);
        const col = index % cols;
        const moves = {
            ArrowLeft: row * cols + Math.max(0, col - 1),
            ArrowRight: row * cols + Math.min(cols - 1, col + 1),
            ArrowUp: Math.max(0, row - 1) * cols + col,
            ArrowDown: Math.min(rows - 1, row + 1) * cols + col,
        };
        if (moves[e.code] !== undefined) {
            e.preventDefault();
            this.setState({ focused: moves[e.code] }, () => this.cellRefs[moves[e.code]]?.focus());
        } else if (['KeyF', 'Enter', 'Space'].includes(e.code)) {
            e.preventDefault();
            if (!e.repeat) this.actOnCell(index, e.code === 'KeyF');
        }
    };

    pressFace = (e) => {
        if (e.button === 0 && !this.paused && ['ready', 'playing'].includes(this.game.status)) {
            this.setState({ pressing: true });
        }
    };

    releaseFace = () => { if (this.state.pressing) this.setState({ pressing: false }); };

    toggleMode = () => this.setState(previous => ({ flagMode: !previous.flagMode }));

    leave = (callback) => {
        this.left = true;
        this.stopClock();
        callback?.();
    };

    back = () => this.leave(this.props.onBack);

    close = () => this.leave(() => this.props.stores.appState.toggleDrawer(false, this.props.drawerLevel || 1));

    renderCell(cell, index) {
        const lost = this.game.status === 'lost';
        const showMine = lost && cell.mine && !cell.flagged;
        const wrongFlag = lost && cell.flagged && !cell.mine;
        const row = Math.floor(index / this.game.cols) + 1;
        const column = index % this.game.cols + 1;
        const classes = ['mine-cell', cell.revealed || showMine ? 'mine-open' : '',
            index === this.game.exploded ? 'mine-exploded' : '', wrongFlag ? 'mine-wrong' : ''].join(' ');
        return (
            <button type='button' key={index} class={classes} data-cell={index}
                data-number={cell.revealed && !cell.mine ? cell.adjacent : undefined}
                tabIndex={index === this.state.focused ? 0 : -1}
                ref={element => { this.cellRefs[index] = element; }}
                onFocus={() => this.setState({ focused: index })}
                onClick={this.revealCell} onContextMenu={this.flagCell}
                onKeyDown={this.onCellKey} onPointerDown={this.pressFace}>
                {cell.flagged && !wrongFlag && <Flag />}
                {(showMine || wrongFlag) && <Mine />}
                {cell.revealed && !cell.mine && cell.adjacent > 0 && <span aria-hidden='true'>{cell.adjacent}</span>}
                <span class='visually-hidden'>
                    <Text id='arcade.mineCellPosition' fields={{ row, column }}>Row {row}, column {column}</Text>{' '}
                    {cell.flagged ? <Text id='arcade.mineFlagged'>Flagged</Text>
                        : showMine ? <Text id='arcade.mineMine'>Mine</Text>
                            : cell.revealed ? <Text id='arcade.mineAdjacent' fields={{ count: cell.adjacent }}>
                                {cell.adjacent} adjacent mines
                            </Text> : <Text id='arcade.mineCovered'>Covered</Text>}
                </span>
            </button>
        );
    }

    render() {
        const {
            elapsedMs, paused, pressing, flagMode, submitting, saveFailed, loadFailed, result, leaderboard,
        } = this.state;
        const { status, rows, cols, mines, flags } = this.game;
        const seconds = Math.min(999, Math.floor(elapsedMs / 1000));
        const remaining = mines - flags;
        const counter = remaining < 0 ? `-${String(Math.min(99, -remaining)).padStart(2, '0')}`
            : String(remaining).padStart(3, '0');
        return (
            <div class='minesweeper-game' data-bs-theme='dark'>
                <h2 class='mine-title'><Text id='arcade.minesweeper'>Minesweeper</Text></h2>
                <div class='mine-difficulties' role='group'>
                    {DIFFICULTIES.map(config => (
                        <button type='button' key={config.key} data-difficulty={config.key}
                            class={`btn btn-sm ${config.key === this.game.key ? 'btn-light' : 'btn-outline-light'}`}
                            aria-pressed={config.key === this.game.key} onClick={this.changeDifficulty}>
                            {config.key === 'beginner' && <Text id='arcade.mineBeginner'>Beginner</Text>}
                            {config.key === 'intermediate' && (
                                <Text id='arcade.mineIntermediate'>Intermediate</Text>
                            )}
                            {config.key === 'expert' && <Text id='arcade.mineExpert'>Expert</Text>}
                        </button>
                    ))}
                </div>
                <div class='mine-window' data-bs-theme='light' style={`--mine-cols: ${cols}; --mine-rows: ${rows};`}>
                    <div class='mine-instruments'>
                        <div class='mine-counter' role='status'>
                            <span class='visually-hidden'>
                                <Text id='arcade.mineRemaining'>Mines remaining</Text>: </span>
                            {counter}
                        </div>
                        <button type='button' class='mine-face' onClick={this.restart} data-reset='true'>
                            <Face status={status} pressing={pressing} />
                            <span class='visually-hidden'><Text id='arcade.mineNewGame'>New game</Text></span>
                        </button>
                        <div class='mine-counter' data-timer='true'>
                            <span class='visually-hidden'><Text id='arcade.mineSeconds'>Seconds</Text>: </span>
                            {String(seconds).padStart(3, '0')}
                        </div>
                    </div>
                    <div class='mine-scroll' ref={element => { this.scrollRef = element; }}>
                        {paused ? (
                            <div class='mine-paused'>
                                <div><Text id='arcade.paused'>Paused — press P or Resume to continue</Text></div>
                                <button type='button' class='btn btn-dark mt-3' onClick={this.resume}>
                                    <Text id='arcade.resume'>Resume</Text>
                                </button>
                            </div>
                        ) : (
                            <div class='mine-board'>
                                {this.game.cells.map((cell, index) => this.renderCell(cell, index))}
                            </div>
                        )}
                    </div>
                </div>
                <div class='mine-actions'>
                    <button type='button' class={`btn btn-sm ${flagMode ? 'btn-warning' : 'btn-outline-light'}`}
                        aria-pressed={flagMode} onClick={this.toggleMode} data-flag-mode='true'>
                        {flagMode ? <Text id='arcade.mineFlagMode'>Flag mode</Text>
                            : <Text id='arcade.mineRevealMode'>Reveal mode</Text>}
                    </button>
                    <button type='button' class='btn btn-sm btn-outline-light' disabled={status !== 'playing'}
                        onClick={paused ? this.resume : this.pause}>
                        {paused ? <Text id='arcade.resume'>Resume</Text> : <Text id='arcade.pause'>Pause</Text>}
                    </button>
                </div>
                <div class='mine-message' aria-live='polite'>
                    {status === 'ready' && <Text id='arcade.mineStart'>Choose any square to begin.</Text>}
                    {status === 'playing' && !paused && (flagMode
                        ? <Text id='arcade.mineFlagHelp'>Tap a covered square to place or remove a flag.</Text>
                        : <Text id='arcade.mineRevealHelp'>Tap to reveal · right-click to flag</Text>)}
                    {status === 'won' && <Text id='arcade.mineWon' fields={{ time: formatTime(elapsedMs) }}>
                        Cleared in {formatTime(elapsedMs)}!
                    </Text>}
                    {status === 'lost' && <Text id='arcade.mineLost'>Boom! Click the face to try again.</Text>}
                    {submitting && <div><Text id='arcade.savingScore'>Saving score…</Text></div>}
                    {saveFailed && <div class='text-warning'>
                        <Text id='arcade.mineSaveFailed'>Your time could not be saved.</Text>
                    </div>}
                    {result?.isNewBest === true && <div class='text-success'>
                        <Text id='arcade.mineNewBest'>New fastest time!</Text>
                    </div>}
                </div>
                <div class='mine-help text-body-secondary'>
                    <Text id='arcade.mineControls'>
                        Match the flags around a number, then click it to reveal its neighbours.
                    </Text>
                    <div><Text id='arcade.mineKeyboard'>
                        Arrows move focus · Enter reveals · F flags · P pauses
                    </Text></div>
                    {cols > 9 && <div><Text id='arcade.mineScroll'>
                        Swipe the board sideways to see more squares.
                    </Text></div>}
                </div>
                <div class='mine-actions'>
                    {this.props.onBack && <button type='button' class='btn btn-outline-light' onClick={this.back}>
                        <Text id='arcade.backToArcade'>Back to arcade</Text>
                    </button>}
                    <button type='button' class='btn btn-outline-light' onClick={this.close}>
                        <Text id='arcade.close'>Close</Text>
                    </button>
                </div>
                {loadFailed && <div class='small text-warning'>
                    <Text id='arcade.mineLoadFailed'>Fastest times could not be loaded.</Text>
                </div>}
                <div class='mine-leaderboard'>
                    <ArcadeLeaderboard stores={this.props.stores} game={this.game.game} timed result={leaderboard} />
                </div>
            </div>
        );
    }
}

export default Minesweeper;
